import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { OptionsList } from "@/components/ui/options-list";
import { cn } from "@/lib/utils";
import type { FilterFieldDefinition, FilterType } from "@/components/ui/filter-bar";

// ─── Constants ────────────────────────────────────────────────────────────────

export const OPERATORS: Record<FilterType, { value: string; label: string }[]> = {
  enum: [
    { value: "is", label: "is" },
    { value: "is_not", label: "is not" },
  ],
  relation: [
    { value: "is", label: "is" },
    { value: "is_not", label: "is not" },
  ],
  text: [
    { value: "contains", label: "contains" },
    { value: "not_contains", label: "doesn't contain" },
    { value: "is", label: "is" },
    { value: "is_not", label: "is not" },
  ],
  number: [
    { value: "eq", label: "=" },
    { value: "neq", label: "≠" },
    { value: "gt", label: ">" },
    { value: "gte", label: "≥" },
    { value: "lt", label: "<" },
    { value: "lte", label: "≤" },
    { value: "between", label: "between" },
  ],
  date: [
    { value: "is", label: "is" },
    { value: "before", label: "before" },
    { value: "after", label: "after" },
    { value: "on", label: "on" },
    { value: "between", label: "between" },
  ],
};

// ─── Operator Picker ──────────────────────────────────────────────────────────

export function OperatorPicker({
  type,
  operator,
  onOperatorChange,
}: {
  type: FilterType;
  operator: string;
  onOperatorChange: (op: string) => void;
}) {
  const operators = OPERATORS[type];
  if (operators.length <= 1) return null;

  return (
    <div className="flex flex-wrap gap-1 px-3 py-2 border-b">
      {operators.map((op) => (
        <button
          key={op.value}
          className={cn(
            "px-2 py-0.5 text-xs rounded-md transition-colors cursor-pointer",
            operator === op.value
              ? "bg-primary text-primary-foreground"
              : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
          )}
          onClick={() => onOperatorChange(op.value)}
        >
          {op.label}
        </button>
      ))}
    </div>
  );
}

// ─── Value Pickers ────────────────────────────────────────────────────────────

export function EnumPicker({
  field,
  selectedValues,
  onValuesChange,
}: {
  field: FilterFieldDefinition;
  selectedValues: string[];
  onValuesChange: (values: string[]) => void;
}) {
  const toggle = (value: string) => {
    onValuesChange(
      selectedValues.includes(value)
        ? selectedValues.filter((v) => v !== value)
        : [...selectedValues, value]
    );
  };
  return (
    <OptionsList
      options={field.options}
      selectedValues={selectedValues}
      onSelect={toggle}
    />
  );
}

export function TextFilterInput({
  selectedValues,
  onValuesChange,
}: {
  field: FilterFieldDefinition;
  selectedValues: string[];
  onValuesChange: (values: string[]) => void;
}) {
  return (
    <div className="p-3">
      <Input
        placeholder="Value..."
        value={selectedValues[0] ?? ""}
        onChange={(e) => onValuesChange(e.target.value ? [e.target.value] : [])}
        className="h-8 text-sm"
      />
    </div>
  );
}

export function NumberFilterInput({
  operator,
  selectedValues,
  onValuesChange,
}: {
  field: FilterFieldDefinition;
  operator: string;
  selectedValues: string[];
  onValuesChange: (values: string[]) => void;
}) {
  if (operator === "between") {
    return (
      <div className="flex gap-2 items-center p-3">
        <Input
          type="number"
          placeholder="Min"
          value={selectedValues[0] ?? ""}
          onChange={(e) => {
            const min = e.target.value;
            const max = selectedValues[1] ?? "";
            onValuesChange(min || max ? [min, ...(max ? [max] : [])] : []);
          }}
          className="h-8 text-sm flex-1"
        />
        <span className="text-xs text-muted-foreground shrink-0">–</span>
        <Input
          type="number"
          placeholder="Max"
          value={selectedValues[1] ?? ""}
          onChange={(e) => {
            const min = selectedValues[0] ?? "";
            const max = e.target.value;
            onValuesChange(min || max ? [min, ...(max ? [max] : [])] : []);
          }}
          className="h-8 text-sm flex-1"
        />
      </div>
    );
  }

  return (
    <div className="p-3">
      <Input
        type="number"
        placeholder="Value..."
        value={selectedValues[0] ?? ""}
        onChange={(e) => onValuesChange(e.target.value ? [e.target.value] : [])}
        className="h-8 text-sm"
      />
    </div>
  );
}

export function DateFilterInput({
  operator,
  selectedValues,
  onValuesChange,
}: {
  field: FilterFieldDefinition;
  operator: string;
  selectedValues: string[];
  onValuesChange: (values: string[]) => void;
}) {
  const presets = [
    { label: "Today", value: "today" },
    { label: "This week", value: "this_week" },
    { label: "This month", value: "this_month" },
    { label: "Overdue", value: "overdue" },
  ];

  if (operator === "is") {
    return (
      <div className="p-3 space-y-2">
        <div className="grid grid-cols-2 gap-1">
          {presets.map((preset) => (
            <Button
              key={preset.value}
              variant={selectedValues.includes(preset.value) ? "default" : "outline"}
              size="sm"
              className="h-7 text-xs"
              onClick={() =>
                onValuesChange(
                  selectedValues.includes(preset.value) ? [] : [preset.value]
                )
              }
            >
              {preset.label}
            </Button>
          ))}
        </div>
        <Input
          type="date"
          value={
            selectedValues[0] && !presets.some((p) => p.value === selectedValues[0])
              ? selectedValues[0]
              : ""
          }
          onChange={(e) => onValuesChange(e.target.value ? [e.target.value] : [])}
          className="h-8 text-sm"
        />
      </div>
    );
  }

  if (operator === "between") {
    return (
      <div className="flex gap-2 items-center p-3">
        <Input
          type="date"
          value={selectedValues[0] ?? ""}
          onChange={(e) => {
            const from = e.target.value;
            const to = selectedValues[1] ?? "";
            onValuesChange(from || to ? [from, ...(to ? [to] : [])] : []);
          }}
          className="h-8 text-sm flex-1"
        />
        <span className="text-xs text-muted-foreground shrink-0">–</span>
        <Input
          type="date"
          value={selectedValues[1] ?? ""}
          onChange={(e) => {
            const from = selectedValues[0] ?? "";
            const to = e.target.value;
            onValuesChange(from || to ? [from, ...(to ? [to] : [])] : []);
          }}
          className="h-8 text-sm flex-1"
        />
      </div>
    );
  }

  return (
    <div className="p-3">
      <Input
        type="date"
        value={selectedValues[0] ?? ""}
        onChange={(e) => onValuesChange(e.target.value ? [e.target.value] : [])}
        className="h-8 text-sm"
      />
    </div>
  );
}

// ─── Value Picker (dispatcher) ────────────────────────────────────────────────

export function ValuePicker({
  field,
  operator,
  selectedValues,
  onValuesChange,
}: {
  field: FilterFieldDefinition;
  operator: string;
  selectedValues: string[];
  onValuesChange: (values: string[]) => void;
}) {
  switch (field.type) {
    case "enum":
    case "relation":
      return (
        <EnumPicker
          field={field}
          selectedValues={selectedValues}
          onValuesChange={onValuesChange}
        />
      );
    case "text":
      return (
        <TextFilterInput
          field={field}
          selectedValues={selectedValues}
          onValuesChange={onValuesChange}
        />
      );
    case "number":
      return (
        <NumberFilterInput
          field={field}
          operator={operator}
          selectedValues={selectedValues}
          onValuesChange={onValuesChange}
        />
      );
    case "date":
      return (
        <DateFilterInput
          field={field}
          operator={operator}
          selectedValues={selectedValues}
          onValuesChange={onValuesChange}
        />
      );
  }
}
